import { FC, useState } from 'react';
import { PayRunListItem } from '../payrun.model';
import DateUtils from 'utils/dateUtils';
import { getAmountWithCurrency } from 'utils/amountUtils';
import routesPath from 'routes/RoutesPath';
import { useNavigate } from 'react-router-dom';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { setCurrentPayPeriodIndex, setNavigationSource, setPayPeriodsList } from '../pay-period-review/payPeriodSlice';
import { PayRunNavigationSource } from '../constants';
import { getFormattedPayPeriodDate, getPayrunStatusColor, getPayrunStatusText } from '../utils';
import FSMSubscriptionEndedModal from 'containers/subscriptions/components/FSMSubscriptionEndedModal';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';

interface PayRunsTableProps {
  payRuns: PayRunListItem[];
}

const PayRunsTable: FC<PayRunsTableProps> = ({ payRuns }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const currentTeam = useAppSelector(selectCurrentTeam);

  const [showSubscriptionEndedModal, setShowSubscriptionEndedModal] = useState(false);

  const { freePlanFrom, isFreePlan } = useFSMPlanInfo();

  const columns = [
    {
      header: 'Pay run ID',
      accessor: 'payRunNumber',
      cell: ({ row }: { row: PayRunListItem }) => <span className="font-semibold">{row.payRunNumber}</span>
    },
    {
      header: 'Pay period (EST)',
      accessor: 'payPeriod',
      cell: ({ row }: { row: PayRunListItem }) => (
        <span className="font-semibold">{getFormattedPayPeriodDate(row.payRunFrom, row.payRunTo)}</span>
      )
    },
    {
      header: 'Members',
      accessor: 'totalMembers'
    },
    {
      header: 'Amount payable',
      accessor: 'amount',
      cell: ({ row }: { row: PayRunListItem }) => <span>{getAmountWithCurrency(row.totalAmount)}</span>
    },
    {
      header: 'Completed by',
      accessor: 'payRunCompletedBy'
    },
    {
      header: 'Completed on',
      accessor: 'completedOn',
      cell: ({ row }: { row: PayRunListItem }) => (
        <span>
          {row.payRunCompletedOn
            ? DateUtils.getDateInFormat({ date: new Date(row.payRunCompletedOn), dateFormat: 'MMM dd, yyyy' })
            : '-'}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'payRunStatus',
      cell: ({ row }: { row: PayRunListItem }) => (
        <span className={`font-bold capitalize ${getPayrunStatusColor(row.payRunStatus)}`}>
          {getPayrunStatusText(row.payRunStatus)}
        </span>
      )
    }
  ];

  const handleCloseSubscriptionEndedModal = () => {
    setShowSubscriptionEndedModal(false);
  };

  const handleRenewSubscription = () => {
    navigate(routesPath.FIELD_SERVICE_MANAGEMENT);
    setShowSubscriptionEndedModal(false);
  };

  const payRunClickHandler = (payRun: PayRunListItem, rowIndex: number) => {
    const isPayRunRestrictedForFreeUser =
      isFreePlan &&
      freePlanFrom &&
      payRun.payRunTo &&
      DateUtils.calculateDaysDifference(payRun.payRunTo, freePlanFrom) > 0;

    if (isPayRunRestrictedForFreeUser) {
      setShowSubscriptionEndedModal(true);
      return;
    }
    dispatch(setNavigationSource(PayRunNavigationSource.LIST));
    dispatch(setCurrentPayPeriodIndex(rowIndex));
    dispatch(setPayPeriodsList(payRuns));
    navigate(
      routesPath.PAY_PERIOD_REVIEW.replace(':teamId', currentTeam?.id.toString() ?? '').replace(
        ':payRunId',
        payRun.payRunId
      )
    );
  };

  return (
    <div className="flex-1 overflow-auto">
      <table className="min-w-full">
        <thead className="bg-highlightGray">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                className="border-t border-borderGray px-6 py-3 text-left text-sbase font-semibold
                 tracking-wider text-primaryText">
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {payRuns.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column, colIndex) => (
                <td
                  key={colIndex}
                  onClick={() => payRunClickHandler(row, rowIndex)}
                  className="cursor-pointer whitespace-nowrap px-6 py-4 text-sbase text-primaryText">
                  {column.cell ? column.cell({ row }) : row[column.accessor]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {showSubscriptionEndedModal && (
        <FSMSubscriptionEndedModal
          handleClose={handleCloseSubscriptionEndedModal}
          onRenew={handleRenewSubscription}
          showCloseButton={true}
        />
      )}
    </div>
  );
};

export default PayRunsTable;
